import { createAsyncThunk, createSelector, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { StoreState, RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { messagingApi } from "messaging/_api/messaging-api";
import { addMessage, removeMessage } from "messaging/_stores/messages-redux-slice";
import AttachmentQuery from "messaging/attachments/_api/queries/attachment-query";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import Message from "messaging/message";

/**
 * The shared files of a chat window, one entry per set of open forums.
 *
 * An entry is keyed by the set, not by one forum, because the server pages the set as one list in
 * one order: a page merged from per-forum pages is not in date order. `getSharedFilesKey` makes the
 * same forums in any order one key, so reordering the tabs does not start a new list.
 */
const initialState: StoreState<PaginatedResponse<MessageFileInfo>> = {
  entries: {},
  loading: {},
  error: {},
};

const KEY_SEPARATOR = ",";
const EMPTY_SHARED_FILES = PaginatedResponse.empty<MessageFileInfo>();

/** Newest first, which is how the server orders the list and how the tab shows it. */
const newestFirst = (a: MessageFileInfo, b: MessageFileInfo) => b.createdDate.diff(a.createdDate) ?? 0;

export function getSharedFilesKey(forumIds: ReadonlyArray<Guid | string | undefined>): string {
  return forumIds
    .map((forumId) => (typeof forumId === "string" ? forumId : forumId?.value))
    .filter((forumId): forumId is string => Boolean(forumId))
    .sort((a, b) => a.localeCompare(b))
    .join(KEY_SEPARATOR);
}

export const populateSharedFiles = createAsyncThunk(
  "attachments/populateSharedFiles",
  async (
    {
      forumIds,
      query,
      append,
    }: {
      forumIds: Guid[];
      query: AttachmentQuery;
      append: boolean;
    },
    thunkAPI
  ) => {
    const key = getSharedFilesKey(forumIds);
    if (!key) return thunkAPI.rejectWithValue("At least one forum id is required");
    const request = thunkAPI.dispatch(
      messagingApi.endpoints.getAttachmentsByForums.initiate({
        forumIds: key.split(KEY_SEPARATOR),
        query,
        append,
      }, { forceRefetch: true })
    );
    const onAbort = () => request.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);
    try {
      return await request.unwrap();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
      request.unsubscribe();
    }
  }
);

/**
 * The entries as the helpers below see them. Inside a reducer they are Immer drafts, which the class
 * type does not accept, so the helpers take them loosely and read each entry back as the class --
 * as `upsertMessage` does in the messages slice.
 */
type SharedFilesState = { entries: Record<string, unknown> };

function entryAt(state: SharedFilesState, key: string): PaginatedResponse<MessageFileInfo> | undefined {
  return state.entries[key] as PaginatedResponse<MessageFileInfo> | undefined;
}

/** The keys of the loaded lists that include this forum. */
function keysIncluding(state: SharedFilesState, forumId: string): string[] {
  return Object.keys(state.entries).filter((key) => key.split(KEY_SEPARATOR).includes(forumId));
}

function withoutFilesOf(files: PaginatedResponse<MessageFileInfo>, messageId: Guid): PaginatedResponse<MessageFileInfo> {
  return files.data
    .filter((file) => file.messageId?.isEqualTo(messageId))
    .reduce((remaining, file) => remaining.removeDataById(file.id), files);
}

/**
 * Brings every loaded list that includes this message's forum up to date with the message.
 *
 * The message replaces whatever files the list held for it. A new message adds its files, an edit
 * that changes them is reflected, and a message the audience can no longer see -- retracted or
 * deleted, which arrives as a tombstone with no attachments -- takes its files out.
 */
function applyMessage(state: SharedFilesState, message: Message): void {
  const forumId = message.forum.id;
  const messageId = message.id;
  if (!forumId || !messageId) return;

  const isVisible = Boolean(message.published) && !message.retracted && !message.isDeleted;
  const files = isVisible ? message.attachments.map((file) => file.sharedIn(forumId, messageId)) : [];
  for (const key of keysIncluding(state, forumId.value)) {
    const entry = entryAt(state, key);
    if (!entry) continue;
    const remaining = withoutFilesOf(entry, messageId);
    state.entries[key] = files.length ? remaining.appendOrReplaceData(files, undefined, newestFirst) : remaining;
  }
}

function withdrawMessage(state: SharedFilesState, messageId: Guid): void {
  for (const key of Object.keys(state.entries)) {
    const entry = entryAt(state, key);
    if (!entry) continue;
    state.entries[key] = withoutFilesOf(entry, messageId);
  }
}

/** One file of one message, with its new processing (#6986). */
export type SharedFileProcessingReport = { forumId: Guid; messageId: Guid; file: MessageFileInfo };

/**
 * Replaces the file in every loaded list that holds it. The list's own copy is kept where nothing
 * holds it: a list that was never loaded reads the processing fresh when it is.
 */
function applyFileProcessing(state: SharedFilesState, report: SharedFileProcessingReport): void {
  const { forumId, messageId, file } = report;
  for (const key of keysIncluding(state, forumId.value)) {
    const entry = entryAt(state, key);
    const held = entry?.data.some((candidate) => candidate.id.isEqualTo(file.id) && candidate.messageId?.isEqualTo(messageId));
    if (!entry || !held) continue;
    state.entries[key] = entry.appendOrReplaceData([file.sharedIn(forumId, messageId)], undefined, newestFirst);
  }
}

const attachmentsSlice = createSlice({
  name: "attachments",
  initialState,
  reducers: {
    /** A file's processing changed (#6986): what the realtime update carries, for every list that holds it. */
    sharedFileProcessingReported: (state, action: PayloadAction<SharedFileProcessingReport>) => {
      applyFileProcessing(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    // Realtime reaches this list through the messages it already delivers: `addMessage` is what the
    // Ably middleware dispatches for a received or updated message, and `removeMessage` for a
    // deleted one. The sender's own writes are handled from their mutations as well, so the list
    // does not wait on the echo. A delete is one of those writes: the batch endpoint answers it with
    // the message's tombstone, and a tombstone takes its files out.
    builder.addCase(addMessage, (state, action) => {
      applyMessage(state, action.payload);
    });
    builder.addCase(removeMessage, (state, action) => {
      withdrawMessage(state, action.payload);
    });
    builder.addMatcher(messagingApi.endpoints.createMessage.matchFulfilled, (state, action) => {
      applyMessage(state, action.payload);
    });
    builder.addMatcher(messagingApi.endpoints.createBulkMessages.matchFulfilled, (state, action) => {
      action.payload.forEach((message) => applyMessage(state, message));
    });
    builder.addMatcher(messagingApi.endpoints.getAttachmentsByForums.matchPending, (state, action) => {
      const key = getSharedFilesKey(action.meta.arg.originalArgs.forumIds);
      if (!key) return;

      state.loading[key] = true;
      state.error[key] = null;
    });
    builder.addMatcher(messagingApi.endpoints.getAttachmentsByForums.matchFulfilled, (state, action) => {
      const { forumIds, append } = action.meta.arg.originalArgs;
      const key = getSharedFilesKey(forumIds);
      if (!key) return;

      const existing = state.entries[key] ?? EMPTY_SHARED_FILES;
      state.entries[key] =
        !existing.isEmpty && append
          ? existing.appendOrReplaceData(action.payload.data, action.payload.headers, newestFirst)
          : action.payload;
      state.loading[key] = false;
    });
    builder.addMatcher(messagingApi.endpoints.getAttachmentsByForums.matchRejected, (state, action) => {
      const key = getSharedFilesKey(action.meta.arg.originalArgs.forumIds);
      if (!key) return;

      state.loading[key] = false;
      state.error[key] = action.error;
    });
  },
});

const selectEntries = (state: RootState) => state.attachments.entries;

const selectSharedFilesByKey = createSelector(
  [selectEntries, (_: RootState, key: string) => key],
  (entries, key) => entries[key] ?? EMPTY_SHARED_FILES
);

export const getSharedFiles = (forumIds: ReadonlyArray<Guid | undefined>) => (state: RootState) =>
  selectSharedFilesByKey(state, getSharedFilesKey(forumIds));
export const getIsLoadingSharedFiles = (forumIds: ReadonlyArray<Guid | undefined>) => (state: RootState) =>
  state.attachments?.loading[getSharedFilesKey(forumIds)] ?? false;
export const getErrorLoadingSharedFiles = (forumIds: ReadonlyArray<Guid | undefined>) => (state: RootState) =>
  state.attachments.error[getSharedFilesKey(forumIds)];
/** Whether a Shared files list that includes the forum is loading a page. */
export const isLoadingSharedFilesOf = (state: RootState, forumId: string): boolean =>
  Object.entries(state.attachments?.loading ?? {}).some(
    ([key, loading]) => loading && key.split(KEY_SEPARATOR).includes(forumId),
  );

export const { sharedFileProcessingReported } = attachmentsSlice.actions;

export default attachmentsSlice;
